import { defineStore, storeToRefs } from "pinia"
import { ref, computed } from "vue"
import { useApiStore } from "@/stores/api"
import { useRouteStore } from "@/stores/route"
import { useSettingsStore } from "@/stores/settings"
import { dayjs } from "@/helpers/dayjs"

export const useTourStore = defineStore(
	"tour",
	() => {
		const api = useApiStore()
		const { tourListOrder } = storeToRefs(useSettingsStore())

		const loadedTours = ref([])

		const reset = () => {
			loadedTours.value = []
		}

		/* load tours functions */

		const loadTours = async () => {
			const response = await api.dispatchApiCall("get", `/api/v2/tours`)
			if (response.data) {
				loadedTours.value = response.data
				return true
			}
			return false
		}

		const createTour = async (data) => {
			return api.dispatchApiCall("post", `/api/v2/tours`, data)
		}

		const updateTour = async (data) => {
			return api.dispatchApiCall("patch", `/api/v2/tours/${data.id}`, data)
		}

		const deleteTour = async (tourId) => {
			return api.dispatchApiCall("delete", `/api/v2/tours/${tourId}`)
		}

		/* access helpers */

		const tours = computed(() => {
			const items = [...loadedTours.value]
			if (tourListOrder.value === 'name.asc') {
				return items.sort((a, b) => {
					if (a.name === null || a.name === undefined) return 1
					if (b.name === null || b.name === undefined) return -1

					return a.name.localeCompare(b.name)
				})
			} else if (tourListOrder.value === 'name.desc') {
				return items.sort((a, b) => {
					if (a.name === null || a.name === undefined) return 1
					if (b.name === null || b.name === undefined) return -1

					return b.name.localeCompare(a.name)
				})
			} else if (tourListOrder.value === 'started_at.asc') {
				return items.sort((a, b) => a.started_at.localeCompare(b.started_at))
			} else if (tourListOrder.value === 'started_at.desc') {
				return items.sort((a, b) => b.started_at.localeCompare(a.started_at))
			} else if (tourListOrder.value === 'duration.asc') {
				return items.sort((a, b) => {
					const durationA = dayjs(a.ended_at).diff(dayjs(a.started_at), 'seconds')
					const durationB = dayjs(b.ended_at).diff(dayjs(b.started_at), 'seconds')
					return durationA - durationB
				})
			} else if (tourListOrder.value === 'duration.desc') {
				return items.sort((a, b) => {
					const durationA = dayjs(a.ended_at).diff(dayjs(a.started_at), 'seconds')
					const durationB = dayjs(b.ended_at).diff(dayjs(b.started_at), 'seconds')
					return durationB - durationA
				})
			}
			return items
		})

		const toursById = computed(() => {
			let returnArray = []
			loadedTours.value.forEach((tour) => {
				returnArray[tour.id] = tour
			})
			return returnArray
		})

		const getLineStringForTour = (tourId) => {
			const { getLineStringForRoute } = useRouteStore()
			const tour = toursById.value[tourId]
			const tourstartedAtTimestamp = tour.started_at ? dayjs(tour.started_at).unix() : 0
			const tourEndedAtTimestamp = tour.ended_at ? dayjs(tour.ended_at).unix() : dayjs().add(10, "year").unix()

			return getLineStringForRoute(tour.tracker_id, tourstartedAtTimestamp, tourEndedAtTimestamp)
		}

		const getRouteForTour = (tourId, columns = null) => {
			const { getRawRoute } = useRouteStore()
			const tour = toursById.value[tourId]
			const tourstartedAtTimestamp = tour.started_at ? dayjs(tour.started_at).unix() : 0
			const tourEndedAtTimestamp = tour.ended_at ? dayjs(tour.ended_at).unix() : dayjs().add(10, "years").unix()

			return getRawRoute(tour.tracker_id, tourstartedAtTimestamp, tourEndedAtTimestamp, columns)
		}

		return {
			reset,
			loadedTours,
			tours,
			toursById,
			loadTours,
			createTour,
			updateTour,
			deleteTour,
			getRouteForTour,
			getLineStringForTour,
		}
	},
	{
		persist: true,
	}
)
